import { useState, type FormEvent } from "react";
import { motion } from "motion/react";
import { useMutation } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { ApiClientError } from "@victor/api-client";
import type {
  SuperAdminMfaSetupResponse,
} from "@victor/types";
import { useAuth } from "../../auth/AuthContext";
import { api } from "../../lib/api";
import { Card } from "../../ui/Card";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Modal } from "../../ui/Modal";

export function MonComptePage() {
  const { superAdmin, reload, logout } = useAuth();

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold tracking-tight text-navy-800 sm:text-4xl">
          Mon compte
        </h1>
        <p className="mt-2 text-navy-600">
          Sécurité de votre compte MyVictor. Le mot de passe et la MFA sont
          requis pour accéder à la console.
        </p>
      </motion.header>

      {superAdmin ? (
        <Card>
          <h2 className="text-lg font-bold text-navy-800">Identité</h2>
          <p className="mt-2 text-navy-700">
            {superAdmin.prenom} {superAdmin.nom}
          </p>
          <p className="text-sm text-navy-500">{superAdmin.email}</p>
          {superAdmin.derniereConnexion ? (
            <p className="mt-2 text-xs text-navy-400">
              Dernière connexion :{" "}
              {new Date(superAdmin.derniereConnexion).toLocaleString("fr-FR")}
            </p>
          ) : null}
        </Card>
      ) : null}

      <ChangePasswordCard onChanged={logout} />

      <MfaCard
        active={superAdmin?.mfaActive ?? false}
        onChanged={() => void reload()}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Change password
// ---------------------------------------------------------------------------
function ChangePasswordCard({ onChanged }: { onChanged: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      api.auth.changePassword({
        currentPassword: current,
        newPassword: next,
      }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setDone(true);
      // Une fois le password changé côté API, toutes les sessions sont
      // révoquées, y compris l'actuelle → on déconnecte pour cohérence.
      setTimeout(() => onChanged(), 2500);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) return;
    mut.mutate();
  }

  return (
    <Card>
      <h2 className="text-lg font-bold text-navy-800">Mot de passe</h2>
      {done ? (
        <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-200">
          Mot de passe changé. Vous allez être déconnecté pour reconnecter
          avec le nouveau.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="mt-3 space-y-3">
          <Input
            type="password"
            label="Mot de passe actuel"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
          <Input
            type="password"
            label="Nouveau mot de passe"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={12}
            hint="Minimum 12 caractères."
          />
          <Input
            type="password"
            label="Confirmation"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            error={
              confirm.length > 0 && confirm !== next
                ? "Les mots de passe ne correspondent pas."
                : undefined
            }
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={
                mut.isPending || next.length < 12 || next !== confirm
              }
            >
              {mut.isPending ? "Mise à jour…" : "Changer"}
            </Button>
          </div>
          {mut.error ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {mut.error instanceof ApiClientError
                ? mut.error.message
                : "Erreur."}
            </p>
          ) : null}
        </form>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// MFA
// ---------------------------------------------------------------------------
function MfaCard({
  active,
  onChanged,
}: {
  active: boolean;
  onChanged: () => void;
}) {
  const [setupResponse, setSetupResponse] =
    useState<SuperAdminMfaSetupResponse | null>(null);
  const [activateCode, setActivateCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const setupMut = useMutation({
    mutationFn: () => api.auth.mfaSetup(),
    onSuccess: (r) => setSetupResponse(r),
  });
  const activateMut = useMutation({
    mutationFn: () => api.auth.mfaActivate({ code: activateCode }),
    onSuccess: (r) => {
      setRecoveryCodes(r.recoveryCodes);
      setSetupResponse(null);
      onChanged();
    },
  });
  const disableMut = useMutation({
    mutationFn: () => api.auth.mfaDisable({ code: disableCode }),
    onSuccess: () => {
      setDisableOpen(false);
      setDisableCode("");
      onChanged();
    },
  });

  return (
    <Card>
      <h2 className="text-lg font-bold text-navy-800">Authentification à 2 facteurs (TOTP)</h2>
      <p className="mt-1 text-sm text-navy-600">
        Obligatoire pour accéder à la console. Compatible Google Authenticator,
        Authy, 1Password, Bitwarden, etc.
      </p>

      {active ? (
        <div className="mt-4 space-y-3">
          <p className="rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-200">
            ✓ MFA active
          </p>
          <Button variant="danger" onClick={() => setDisableOpen(true)}>
            Désactiver la MFA
          </Button>
        </div>
      ) : setupResponse ? (
        <div className="mt-4 space-y-3">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-white p-4 ring-1 ring-navy-100">
            <div className="bg-white p-2 rounded-lg ring-1 ring-navy-100">
              <QRCodeSVG value={setupResponse.otpauthUrl} size={180} />
            </div>
            <p className="text-xs text-navy-500 text-center">
              Scannez ce QR avec votre app TOTP. Si vous ne pouvez pas, saisissez
              le secret manuel :
            </p>
            <code className="text-xs font-mono bg-navy-50 px-2 py-1 rounded text-navy-700">
              {setupResponse.secret}
            </code>
          </div>
          <Input
            label="Code généré par l'app (6 chiffres)"
            value={activateCode}
            onChange={(e) => setActivateCode(e.target.value)}
            placeholder="123 456"
            autoComplete="one-time-code"
          />
          <Button
            fullWidth
            disabled={activateMut.isPending || activateCode.length < 6}
            onClick={() => activateMut.mutate()}
          >
            {activateMut.isPending ? "Activation…" : "Activer la MFA"}
          </Button>
          {activateMut.error ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {activateMut.error instanceof ApiClientError
                ? activateMut.error.message
                : "Erreur."}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-4">
          <Button
            onClick={() => setupMut.mutate()}
            disabled={setupMut.isPending}
          >
            {setupMut.isPending ? "Préparation…" : "Activer la MFA"}
          </Button>
        </div>
      )}

      {/* Recovery codes (affichés UNE fois) */}
      <Modal
        open={!!recoveryCodes}
        onClose={() => setRecoveryCodes(null)}
        title="Codes de récupération"
        size="md"
      >
        <p className="text-sm text-navy-600">
          Conservez ces 10 codes en lieu sûr. Chacun ne peut être utilisé qu'une
          fois pour vous connecter si vous perdez l'accès à votre app TOTP.
          <strong> Ils ne seront plus affichés.</strong>
        </p>
        {recoveryCodes ? (
          <ul className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm">
            {recoveryCodes.map((c) => (
              <li
                key={c}
                className="rounded-lg bg-navy-50 px-3 py-2 text-navy-800 ring-1 ring-navy-100"
              >
                {c}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              void navigator.clipboard.writeText(
                (recoveryCodes ?? []).join("\n"),
              )
            }
          >
            Copier tous
          </Button>
          <Button onClick={() => setRecoveryCodes(null)}>
            J'ai sauvegardé les codes
          </Button>
        </div>
      </Modal>

      {/* Disable */}
      <Modal
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        title="Désactiver la MFA"
        size="md"
      >
        <p className="text-sm text-navy-600">
          Pour désactiver la MFA, saisissez un code valide depuis votre app
          TOTP. Toutes les sessions actives resteront ouvertes.
        </p>
        <div className="mt-4 space-y-3">
          <Input
            label="Code TOTP"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="123 456"
            autoComplete="one-time-code"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDisableOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={() => disableMut.mutate()}
              disabled={disableMut.isPending || disableCode.length < 6}
            >
              {disableMut.isPending ? "Désactivation…" : "Désactiver"}
            </Button>
          </div>
          {disableMut.error ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {disableMut.error instanceof ApiClientError
                ? disableMut.error.message
                : "Erreur."}
            </p>
          ) : null}
        </div>
      </Modal>
    </Card>
  );
}
